import React from 'react';
import pt from 'prop-types';

import api from 'services/api';

import ViewLoading from 'components/view/loading';
import EarningsPage from 'partners/components/pages/earnings/earnings';

const PAYOUT_STATUS_POLL_MS = 4000;

export default class Earnings extends React.PureComponent {
  static propTypes = {
    location: pt.object.isRequired,
    router: pt.object.isRequired,
  };

  static contextTypes = {
    notifyError: pt.func.isRequired,
  };

  state = {
    loaded: false,
    earnings: null,
    payouts: null,
    onboarding: false,
    setupModal: null, // 'intro' | 'waiting' | 'verifying' | 'connected'
    payoutModal: null,
    payoutEarnings: null,
    managingPayouts: false,
    accountModal: false,
    accountDetails: null,
    accountLoading: false,
    confirmDisconnect: false,
    disconnecting: false,
  };

  componentDidMount() {
    const { location, router } = this.props;

    window.addEventListener('message', this.onWindowMessage);

    // Returning from Stripe onboarding: when this tab was opened from the
    // Earnings page, notify the original window and close this one
    if (location?.query?.setup === 'return') {
      if (window.opener) {
        try {
          window.opener.postMessage(
            { type: 'swell:payout-setup-return' },
            window.location.origin,
          );
        } catch (err) {
          // Original window is gone - continue loading normally
        }

        window.close();
      }

      router.replace(location.pathname);
    }

    this.fetchData();
  }

  componentWillUnmount() {
    this.stopPolling();
    window.removeEventListener('message', this.onWindowMessage);
  }

  onWindowMessage = (event) => {
    if (
      event.origin === window.location.origin &&
      event.data?.type === 'swell:payout-setup-return'
    ) {
      // The user is back from Stripe - reflect that right away while
      // verification completes
      this.setState((state) =>
        state.setupModal === 'waiting' ? { setupModal: 'verifying' } : null,
      );

      this.checkPayoutStatus();
    }
  };

  async fetchData() {
    try {
      const [earnings, payouts] = await Promise.all([
        api.get('/partner/earnings'),
        api.get('/partner/payouts'),
      ]);

      this.setState({ earnings, payouts, loaded: true });
    } catch (err) {
      this.context.notifyError(err.message);
      this.setState({ loaded: true });
    }
  }

  onClickSetupPayouts = (event) => {
    if (event) {
      event.preventDefault();
    }

    this.setState({ setupModal: 'intro' });
  };

  onClickManagePayouts = async (event) => {
    if (event) {
      event.preventDefault();
    }

    this.setState({
      accountModal: true,
      accountDetails: null,
      accountLoading: true,
    });

    try {
      const accountDetails = await api.get('/partner/payouts/account');

      this.setState({ accountDetails: accountDetails || {} });
    } catch (err) {
      this.context.notifyError(err.message);
    }

    // Always clear loading so the modal actions stay usable, even when
    // account details could not be loaded
    this.setState({ accountLoading: false });
  };

  onCloseAccountModal = () => {
    this.setState({ accountModal: false, confirmDisconnect: false });
  };

  onClickStripeDashboard = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (this.state.managingPayouts) {
      return;
    }

    this.setState({ managingPayouts: true });

    try {
      const result = await api.post('/partner/payouts/dashboard');

      if (result?.url) {
        window.open(result.url, '_blank');
      } else {
        this.context.notifyError('Unable to open payout settings');
      }
    } catch (err) {
      this.context.notifyError(err.message);
    }

    this.setState({ managingPayouts: false });
  };

  onClickDisconnect = () => {
    this.setState({ confirmDisconnect: true });
  };

  onCancelDisconnect = () => {
    this.setState({ confirmDisconnect: false });
  };

  onConfirmDisconnect = async () => {
    if (this.state.disconnecting) {
      return;
    }

    this.setState({ disconnecting: true });

    try {
      await api.post('/partner/payouts/disconnect');

      this.setState({
        accountModal: false,
        confirmDisconnect: false,
        accountDetails: null,
      });

      await this.fetchData();
    } catch (err) {
      this.context.notifyError(err.message);
    }

    this.setState({ disconnecting: false });
  };

  onClickPayout = async (event) => {
    const payoutId = event.currentTarget.dataset.id;
    const payout = this.state.payouts?.results?.find(
      (record) => String(record.id) === payoutId,
    );

    if (!payout) {
      return;
    }

    this.setState({ payoutModal: payout, payoutEarnings: null });

    try {
      const payoutEarnings = await api.get('/partner/earnings', {
        payout_id: payoutId,
      });

      this.setState({ payoutEarnings });
    } catch (err) {
      this.context.notifyError(err.message);
      this.setState({ payoutEarnings: { results: [] } });
    }
  };

  onClosePayoutModal = () => {
    this.setState({ payoutModal: null, payoutEarnings: null });
  };

  onCloseSetupModal = () => {
    this.stopPolling();
    this.setState({ setupModal: null, onboarding: false });
  };

  onClickContinueToStripe = async () => {
    if (this.state.onboarding) {
      return;
    }

    this.setState({ onboarding: true });

    try {
      const result = await api.post('/partner/payouts/onboard');

      if (result?.url) {
        window.open(result.url, '_blank');
        this.setState({ setupModal: 'waiting', onboarding: false });
        this.startPolling();
        return;
      }

      this.context.notifyError('Unable to start payout setup');
    } catch (err) {
      this.context.notifyError(err.message);
    }

    this.setState({ onboarding: false });
  };

  startPolling() {
    this.stopPolling();
    this.pollInterval = setInterval(
      this.checkPayoutStatus,
      PAYOUT_STATUS_POLL_MS,
    );
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  checkPayoutStatus = async () => {
    try {
      const status = await api.get('/partner/payouts/status');

      if (status?.enabled) {
        this.stopPolling();

        // Show the connected state only when the setup modal is waiting on it
        this.setState((state) =>
          state.setupModal === 'waiting' || state.setupModal === 'verifying'
            ? { setupModal: 'connected' }
            : null,
        );

        this.fetchData();
        return;
      }

      if (status?.details_submitted) {
        // Details are in - Stripe is verifying the account
        this.setState((state) =>
          state.setupModal === 'waiting' ? { setupModal: 'verifying' } : null,
        );
      }
    } catch (err) {
      // Keep polling; transient errors are retried on the next tick
    }
  };

  render() {
    if (!this.state.loaded) {
      return <ViewLoading />;
    }

    return (
      <EarningsPage
        {...this.props}
        earnings={this.state.earnings}
        payouts={this.state.payouts}
        onboarding={this.state.onboarding}
        setupModal={this.state.setupModal}
        payoutModal={this.state.payoutModal}
        payoutEarnings={this.state.payoutEarnings}
        onClickSetupPayouts={this.onClickSetupPayouts}
        onClickContinueToStripe={this.onClickContinueToStripe}
        onCloseSetupModal={this.onCloseSetupModal}
        onClickPayout={this.onClickPayout}
        onClosePayoutModal={this.onClosePayoutModal}
        managingPayouts={this.state.managingPayouts}
        onClickManagePayouts={this.onClickManagePayouts}
        accountModal={this.state.accountModal}
        accountDetails={this.state.accountDetails}
        accountLoading={this.state.accountLoading}
        confirmDisconnect={this.state.confirmDisconnect}
        disconnecting={this.state.disconnecting}
        onCloseAccountModal={this.onCloseAccountModal}
        onClickStripeDashboard={this.onClickStripeDashboard}
        onClickDisconnect={this.onClickDisconnect}
        onCancelDisconnect={this.onCancelDisconnect}
        onConfirmDisconnect={this.onConfirmDisconnect}
      />
    );
  }
}
